import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import "./admin.css";

/**
 * A CASCA do super admin.
 *
 * Duas famílias na sidebar, de propósito:
 *   ACERVO  — o que a criança vê (é o que o CMS antigo fazia)
 *   NEGÓCIO — quem paga, o que se vende, e como as pessoas chegam
 *
 * O tema é uma troca de VARIÁVEIS CSS no elemento raiz (não um tema paralelo),
 * então nenhuma tela precisa saber que o tema existe.
 */

/**
 * ÍCONES — SVG inline.
 *
 * ⚠️ POR QUE NÃO USAR "▤ ≡ ◈ ⋔ ☺" COMO ÍCONE (era exatamente o que estava aqui):
 * aquilo são glifos TIPOGRÁFICOS, desenhados pela fonte do sistema — não ícones.
 *   1. Peso/tamanho/alinhamento mudam de máquina para máquina: a sidebar parece quebrada.
 *   2. Onde a fonte não tem o glifo, aparece TOFU (▯).
 *   3. "≡" é o símbolo matemático "idêntico a" e "⋔" é um pitchfork; "☺" vira um EMOJI
 *      COLORIDO em vários sistemas, destoando de tudo.
 *
 * SVG resolve os três: desenho nosso, traço constante, e `currentColor` herda a cor do
 * estado (ativo/inativo) sem precisar de CSS extra.
 */
const CAMINHOS = {
  home: (
    <>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5.5 9.5V20.5h13V9.5" />
      <path d="M9.5 20.5V14h5v6.5" />
    </>
  ),
  series: (
    <>
      <rect x="2.5" y="4.5" width="19" height="15" rx="2" />
      <path d="M7 4.5v15M17 4.5v15M2.5 12h19" />
    </>
  ),
  conteudos: (
    <>
      <path d="M8 6h13M8 12h13M8 18h13" />
      <path d="M3.5 6h.01M3.5 12h.01M3.5 18h.01" />
    </>
  ),
  banners: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="8.5" cy="10" r="1.5" />
      <path d="m3 16.5 4.5-4 3.5 3 4-4.5L21 16.5" />
    </>
  ),
  templates: (
    <>
      <rect x="8" y="8" width="12" height="12" rx="2" />
      <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" />
    </>
  ),
  visao: (
    <>
      <rect x="3" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.5" />
    </>
  ),
  usuarios: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
      <path d="M16 4.6a3.5 3.5 0 0 1 0 6.8" />
      <path d="M17.5 14.2A6.5 6.5 0 0 1 21.5 20" />
    </>
  ),
  catalogo: (
    <>
      <path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0l-7.2-7.2A2 2 0 0 1 3 12V4.8A1.8 1.8 0 0 1 4.8 3H12a2 2 0 0 1 1.4.6l7.2 7.2a2 2 0 0 1 0 2.6Z" />
      <circle cx="7.6" cy="7.6" r="1.3" />
    </>
  ),
  funil: <path d="M3.5 4.5h17l-6.6 7.7v5.9l-3.8 2.4v-8.3Z" />,
  perfis: (
    <>
      <circle cx="12" cy="7.5" r="3.3" />
      <path d="M5.5 20a6.5 6.5 0 0 1 13 0" />
      <path d="M9 3.6a3.3 3.3 0 0 0 6 0" />
    </>
  ),
  pagamentos: (
    <>
      <rect x="2.5" y="5.5" width="19" height="13" rx="2.5" />
      <path d="M2.5 9.5h19" />
      <path d="M6 14.5h4" />
    </>
  ),
  dados: (
    <>
      <ellipse cx="12" cy="5.5" rx="7.5" ry="3" />
      <path d="M4.5 5.5v6c0 1.7 3.4 3 7.5 3s7.5-1.3 7.5-3v-6" />
      <path d="M4.5 11.5v6c0 1.7 3.4 3 7.5 3s7.5-1.3 7.5-3v-6" />
    </>
  ),
  webhooks: <path d="M13.2 2.5 4.5 13.6h6.6l-1.3 7.9 8.7-11.1h-6.6z" />,
  voltar: (
    <>
      <path d="M9 14 4 9l5-5" />
      <path d="M20 20v-7a4 4 0 0 0-4-4H4" />
    </>
  ),
};

function Icone({ nome }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="17"
      height="17"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      // decorativo: o rótulo ao lado já diz o que é. Anunciar de novo é ruído no leitor de tela.
      aria-hidden="true"
      focusable="false"
    >
      {CAMINHOS[nome]}
    </svg>
  );
}

const ACERVO = [
  { to: "/admin/home", label: "Home (Seções)", icon: "home" },
  { to: "/admin/series", label: "Séries", icon: "series" },
  { to: "/admin/conteudos", label: "Conteúdos", icon: "conteudos" },
  { to: "/admin/banners", label: "Banners", icon: "banners" },
  { to: "/admin/templates", label: "Templates", icon: "templates" },
];

const NEGOCIO = [
  { to: "/admin", label: "Visão geral", icon: "visao", end: true },
  { to: "/admin/usuarios", label: "Usuários & Assinaturas", icon: "usuarios" },
  { to: "/admin/pagamentos", label: "Pagamentos", icon: "pagamentos" },
  { to: "/admin/planos", label: "Planos (lojas)", icon: "catalogo" },
  { to: "/admin/dados", label: "Dados (tabelas)", icon: "dados" },
  { to: "/admin/catalogo", label: "Catálogo", icon: "catalogo" },
  { to: "/admin/funil", label: "Funil de entrada", icon: "funil" },
  { to: "/admin/perfis", label: "Perfis (funil)", icon: "perfis" },
  { to: "/admin/webhooks", label: "Webhooks", icon: "webhooks" },
  { to: "/admin/seguranca", label: "Segurança (2FA)", icon: "usuarios" },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const [tema, setTema] = useState(
    () => localStorage.getItem("admTema") || "dark"
  );

  useEffect(() => {
    localStorage.setItem("admTema", tema);
  }, [tema]);

  const email = (() => {
    try {
      const t = localStorage.getItem("token");
      const p = JSON.parse(atob(t.split(".")[1]));
      return p.sub || p.email || "";
    } catch {
      return "";
    }
  })();

  // Mascara o email: o painel fica aberto em telas compartilhadas.
  const emailMascarado = email.replace(/^(.{2})[^@]*(@.*)$/, "$1***$2");
  const iniciais = (email[0] || "A").toUpperCase() + (email[1] || "").toUpperCase();

  const item = (i) => (
    <NavLink
      key={i.to}
      to={i.to}
      end={i.end}
      className={({ isActive }) =>
        `adm__navItem ${isActive ? "adm__navItem--on" : ""}`
      }
    >
      <span className="adm__navIcon">
        <Icone nome={i.icon} />
      </span>
      <span>{i.label}</span>
    </NavLink>
  );

  return (
    <div className="adm" data-theme={tema}>
      <aside className="adm__side">
        <div className="adm__brand">
          {/* A logo OFICIAL — a mesma que o site usa no header. Antes aqui havia uma marca
              improvisada (quadrado vermelho + play desenhado à mão + texto), que não é a
              identidade da marca. Ver a nota sobre tema claro em .adm__brandPlate. */}
          <span className="adm__brandPlate">
            <img
              src="/imgs/reino_header_logo.svg"
              alt="Reinoplay"
              className="adm__brandLogo"
            />
          </span>
        </div>
        <div className="adm__brandSub">SUPER ADMIN</div>

        <nav className="adm__nav">
          <div className="adm__navGroup">
            <div className="adm__navLabel">ACERVO</div>
            {ACERVO.map(item)}
          </div>
          <div className="adm__navGroup">
            <div className="adm__navLabel">NEGÓCIO</div>
            {NEGOCIO.map(item)}
          </div>

          {/* O caminho de VOLTA. Enquanto os dois painéis convivem, ninguém pode ficar
              preso aqui — se faltar algo, a pessoa volta pro que já funciona, sem pedir
              socorro. Este item some quando o painel antigo for aposentado. */}
          <div className="adm__navGroup" style={{ borderTop: "1px solid var(--border)", paddingTop: 10 }}>
            <button
              className="adm__navItem"
              onClick={() => navigate("/manager")}
              title="Voltar para o painel que você já conhece"
            >
              <span className="adm__navIcon">
                <Icone nome="voltar" />
              </span>
              <span>Painel antigo</span>
            </button>
          </div>
        </nav>

        <button
          className="adm__user"
          style={{
            background: "none",
            border: "none",
            borderTop: "1px solid var(--border)",
            cursor: "pointer",
            font: "inherit",
            textAlign: "left",
            width: "100%",
          }}
          onClick={() => navigate("/")}
          title="Voltar para o site"
        >
          <span className="adm__avatar">{iniciais}</span>
          <div>
            <div className="adm__userName">Super Admin</div>
            <div className="adm__userMail">{emailMascarado}</div>
          </div>
        </button>
      </aside>

      <main className="adm__main">
        <Outlet context={{ tema, setTema }} />
      </main>
    </div>
  );
}

/**
 * Cabeçalho da página. Cada tela usa este — assim o botão de tema fica sempre
 * no mesmo lugar e o título/subtítulo têm o mesmo peso em todas.
 */
export function AdminHeader({ titulo, subtitulo, acoes, tema, setTema }) {
  return (
    <header className="adm__top">
      <div style={{ flex: 1, minWidth: 0 }}>
        <h1>{titulo}</h1>
        {subtitulo && <p>{subtitulo}</p>}
      </div>
      {acoes}
      <button
        className="adm__themeBtn"
        onClick={() => setTema(tema === "dark" ? "light" : "dark")}
      >
        {tema === "dark" ? "☀" : "☾"}
        <span>{tema === "dark" ? "Tema claro" : "Tema escuro"}</span>
      </button>
    </header>
  );
}
